import type { ReactElement } from 'react';
import { acaoBloqueavel, atributosDeBloqueio, type MotivoBloqueio } from '@/lib/bloqueio';
import { cn } from '@/lib/utils';
import { ICONE_POR_MEIO } from '../pagamento/iconePorMeio';
import { haPagamentoVivo } from '../../domain/pagamento/saldoPagamento';
import { conflitaComOutroPar } from '../../domain/vendaRapida/conflitoComVenda';
import type { AtalhoVendaRapida, ListaAtalhos, TeclaAtalho } from '../../domain/vendaRapida/tipos';
import { useVendaStore } from '../../stores/vendaStore';
import { AVISO_ATALHO_SEM_ITENS, AVISO_PAGAMENTO_JA_INICIADO } from './avisosVendaRapida';
import { useAcionarCenario } from './useAcionarCenario';

/**
 * Faixa "Métodos de pagamento rápidos" do Pencil (nó `I10H4d` de
 * `design/CentriumCheckout.pen`, export em
 * `design/HTML - Pencil/CentriumCheckout.html`): linha de 36px no **topo** do
 * cartão "Pagamento e totais" (`OzP7o`), acima do cabeçalho — `top: 16`, antes
 * do `top: 60` do título.
 *
 * Tradução fiel do desenho, com os tokens equivalentes de `global.css` (nenhum
 * hex solto):
 *
 * | Pencil | Aqui |
 * |---|---|
 * | linha 36px, `gap: 8`, botões `flex: 1 1 0` | `h-9`, `gap-[8px]`, `flex-1` |
 * | raio 12, gap interno 6, conteúdo centralizado | `rounded-xl gap-[6px] justify-center` |
 * | ícone de 14px (reicon, ver AD-201) | `size-3.5` |
 * | rótulo Inter 12/600 | `text-xs font-semibold` |
 * | todos `#EEF0F3`/`#0A0B0D`, ícone `#5B616E` | `bg-secondary text-secondary-foreground`, ícone `text-muted-foreground` |
 *
 * **Divergência deliberada do Pencil: todos os botões são cinza.** O desenho
 * pinta o primeiro ("PIX (F6)") em azul e os outros três em cinza, e a primeira
 * implementação reproduziu isso destacando o primeiro da lista. **Corrigido a
 * pedido do usuário (2026-09-05).** O desenho fixou um mockup com métodos
 * conhecidos, em que o azul dizia "este é o principal"; aqui a faixa é
 * populada pelo cadastro do ERP, e a ordem é a das teclas — o primeiro da lista
 * é só quem calhou de estar em F6. Pintá-lo de azul afirmaria uma hierarquia
 * que nenhum dado sustenta, e o operador leria como "o recomendado".
 *
 * **A UI não filtra, não ordena e não reinterpreta nada**
 * (`contracts/venda-rapida-domain-api.md` §6): recebe `ListaAtalhos` pronta.
 *
 * **Só apresentação desde a feature 016.** A faixa registrava as teclas F6–F9,
 * e por isso "não exibir" e "não acionar" eram o mesmo fato no mobile, onde ela
 * não é montada. A 016 separou os dois (`FR-011`/`FR-012`): as teclas passaram
 * para `TeclasVendaRapida`, montado em `AppShell` nos dois layouts, e esta
 * faixa continua restrita ao desktop **por montagem** — só `DesktopLayout`
 * monta `PainelPagamentoETotais`. Continua sem `if (isMobile)` neste arquivo.
 */

interface BotaoAtalhoProps {
  readonly atalho: AtalhoVendaRapida;
  readonly onAcionar: () => void;
  /** `null` = acionável; texto = frase que o operador lê ao clicar. */
  readonly bloqueio: MotivoBloqueio;
}

function BotaoAtalho({ atalho, onAcionar, bloqueio }: BotaoAtalhoProps): ReactElement {
  const Icone = ICONE_POR_MEIO[atalho.meioPagtoNFe];

  return (
    <button
      type="button"
      data-testid={`atalho-venda-rapida-${atalho.tecla}`}
      {...atributosDeBloqueio(bloqueio)}
      onClick={acaoBloqueavel(bloqueio, onAcionar)}
      // O rótulo visível já diz "Nome (F6)"; o acessível explicita o gesto, que
      // é o que um leitor de tela não infere de um parêntese.
      aria-label={`${atalho.nome} — atalho ${atalho.tecla}`}
      // Bloqueado, o `title` é o **motivo** — é a informação de que o operador
      // precisa, e o rótulo do atalho já está escrito no próprio botão. Depois
      // do spread de propósito: `atributosDeBloqueio` também traz `title`, e a
      // ordem inversa devolveria o nome do atalho por cima do motivo.
      title={bloqueio ?? `${atalho.nome} (${atalho.tecla})`}
      className={cn(
        'flex h-9 min-w-0 flex-1 items-center justify-center gap-[6px] rounded-xl px-2',
        'text-xs font-semibold transition-colors outline-none',
        'focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
        // Mesmo tratamento das opções bloqueadas do combobox de pagamento
        // (`SeletorCondicaoForma`): apagado e com o cursor recusando, sem sumir
        // da faixa — o operador precisa continuar vendo quais cenários existem.
        'aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-secondary',
      )}
    >
      <Icone className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="truncate">
        {atalho.nome} ({atalho.tecla})
      </span>
    </button>
  );
}

export interface DicaAtalhosProps {
  /** Já projetada (T007): a faixa não filtra, ordena nem reinterpreta nada. */
  readonly atalhos: ListaAtalhos;
  /** O **mesmo** comando da tecla e do clique — não há caminho alternativo. */
  readonly onAcionar: (tecla: TeclaAtalho) => void;
  /**
   * Motivo pelo qual a faixa inteira está bloqueada, ou `null`.
   *
   * Chega por prop, e não é lido do store aqui, pelo mesmo motivo de `atalhos`:
   * este componente não decide nada. Quem responde é `FaixaAtalhosVendaRapida`.
   *
   * Vale para os quatro botões de uma vez porque as duas causas — venda sem item
   * e venda com forma de pagamento já lançada (AD-280) — são da venda, não de um
   * cenário: nenhum atalho funciona numa venda vazia nem numa já em pagamento.
   * Tem precedência sobre `bloqueioPorAtalho`.
   */
  readonly bloqueio?: MotivoBloqueio;
  /**
   * Motivo que vale para **um** cenário, ou `null`. É a causa que depende do par
   * (condição, forma) do atalho: com a condição "Cartão" escolhida, o atalho de
   * "À vista" recusa e o de "Cartão" não — bloquear a faixa inteira apagaria o
   * único que funciona. Decidido por quem lê a venda, pelo mesmo motivo do
   * `bloqueio`.
   */
  readonly bloqueioPorAtalho?: (atalho: AtalhoVendaRapida) => MotivoBloqueio;
}

/**
 * Presentacional, no mesmo espírito de `BotaoFinalizarVenda`: quem possui o
 * comando é `FaixaAtalhosVendaRapida`, logo abaixo. A separação existe para o
 * componente ser exercitável sem o provider de finalização nem a query do
 * catálogo — e para deixar óbvio, na assinatura, que ele não decide nada.
 */
export function DicaAtalhos({
  atalhos,
  onAcionar,
  bloqueio = null,
  bloqueioPorAtalho,
}: DicaAtalhosProps): ReactElement | null {
  // Sem atalho, a área inteira é omitida (`FR-016`) — nada de faixa vazia nem
  // de mensagem de erro: catálogo ausente é um desfecho normal (I4).
  if (atalhos.length === 0) {
    return null;
  }

  return (
    <div
      className="flex h-9 w-full shrink-0 items-center gap-[8px]"
      data-testid="dica-atalhos-venda-rapida"
    >
      {atalhos.map((atalho) => (
        <BotaoAtalho
          key={atalho.tecla}
          atalho={atalho}
          onAcionar={() => {
            onAcionar(atalho.tecla);
          }}
          bloqueio={bloqueio ?? bloqueioPorAtalho?.(atalho) ?? null}
        />
      ))}
    </div>
  );
}

/**
 * A faixa ligada à venda: lista da sessão + comando real. É este componente que
 * `PainelPagamentoETotais` monta no topo do cartão.
 */
export function FaixaAtalhosVendaRapida(): ReactElement | null {
  const { atalhos, acionar } = useAcionarCenario();

  /**
   * Item **ativo** no grid: linha cancelada não conta (correção do usuário,
   * 2026-09-10).
   *
   * É a mesma leitura de `vendaTemItens` em `criarDepsPadrao` — a guarda G3 de
   * `acionarCenario`, que já recusava com `AVISO_ATALHO_SEM_ITENS`. O que
   * faltava era a faixa **parecer** bloqueada antes do gesto: os botões
   * seguiam com o mesmo azul-cinza dos acionáveis, e o operador só descobria a
   * regra ao clicar. Aqui a leitura devolve um booleano, então o seletor não
   * recria referência a cada render.
   *
   * A frase é a mesma constante que o comando usa ao recusar: duas redações do
   * mesmo motivo divergiriam no dia em que só uma fosse revisada.
   */
  const semItemAtivo = useVendaStore((estado) => !estado.linhas.some((linha) => !linha.cancelada));

  /**
   * Forma viva na venda: nenhum cenário lança mais (pedido do usuário,
   * 2026-10-16, AD-280: "já que não pode usar mesmo").
   *
   * É a primeira metade da guarda G5 de `acionarCenario` — a que vale para os
   * quatro botões de uma vez, e por isso vai no `bloqueio` da faixa.
   */
  const comFormaAplicada = useVendaStore((estado) => haPagamentoVivo(estado.pagamentos));

  /**
   * A outra metade de G5: condição **diferente** da do cenário (correção do
   * usuário, 2026-10-19, AD-287). Escolher uma condição e clicar num cenário de
   * outra dava erro, com o botão idêntico ao acionável. Esta metade é **por
   * atalho** — o cenário da mesma condição precisa seguir acionável para repetir
   * um lançamento que falhou (ver o TSDoc de G5) —, então vai em
   * `bloqueioPorAtalho`, pela mesma `conflitaComOutroPar` que o comando usa.
   *
   * Seletor primitivo: o componente só renderiza de novo quando a condição muda.
   */
  const condicaoCodigo = useVendaStore((estado) => estado.condicaoSelecionada?.codigo ?? null);

  return (
    <DicaAtalhos
      atalhos={atalhos}
      onAcionar={(tecla) => {
        void acionar(tecla);
      }}
      bloqueio={motivoDeBloqueioDaFaixa(semItemAtivo, comFormaAplicada)}
      bloqueioPorAtalho={(atalho) =>
        conflitaComOutroPar(atalho, { condicaoCodigo, temFormaAplicada: comFormaAplicada })
          ? AVISO_PAGAMENTO_JA_INICIADO
          : null
      }
    />
  );
}

/**
 * A frase da faixa bloqueada, na ordem das guardas de `acionarCenario` (G3 antes
 * de G5) — a mesma frase que o gesto diria, para o botão apagado e a tecla nunca
 * divergirem.
 */
export function motivoDeBloqueioDaFaixa(
  semItemAtivo: boolean,
  comFormaAplicada: boolean,
): MotivoBloqueio {
  if (semItemAtivo) {
    return AVISO_ATALHO_SEM_ITENS;
  }
  if (comFormaAplicada) {
    return AVISO_PAGAMENTO_JA_INICIADO;
  }
  return null;
}
